import {
  useCallback, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useSelectGroupingToolGroup } from '~/_shared/hooks/grouping/useSelectGroupingToolGroup.hook';
import {
  type BucketKey,
  type GroupingColumn, GroupingType, isGroupingColumnNumeric, isGroupingColumnText,
  type TextGroupKey,
} from '~/_shared/types/grouping/grouping';
import { getNumericActiveGroupColumnType } from '~/_shared/types/grouping/numericActiveGroupColumnType.helpers';
import {
  isCustomMarkerVisualSettings,
  isStandardMarkerVisualSettings, MarkerStyleType,
} from '~/_shared/types/marker.types';
import { MarkerSettingType } from '~/_shared/types/markers/visualSettings.enums';
import {
  type MarkerSettings, type MarkersVisualSettings,
} from '~/_shared/types/markers/visualSettings.types';
import { type SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { filterNonNumericBuckets } from '~/_shared/utils/grouping/grouping.helpers';
import { useGetGroupInfoBySpreadsheetRowId } from '~/_shared/utils/grouping/useGetGroupInfoBySpreadsheetRowId.hook';
import {
  type GroupInfo,
  type NumericGroupInfo,
  type TextGroupInfo,
  useGetSpreadsheetRowIdsByGroupInfo,
} from '~/_shared/utils/grouping/useGetSpreadsheetRowIdsByGroupInfo.hook';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  createNumericBucketKey,
  createTextGroupKey,
  getDefaultMarkerSettingsForGroup,
  getDefaultStyleIdForSecondaryTextGroup, getTextGroupKeyHierarchyIndicator, removeAboveLabelVisualSettings,
  syncMarkerColorAndLabelBackgroundVisualSettings,
} from '~/_shared/utils/markers/markersVisualSettings.helpers';
import {
  DEFAULT_MARKER_SIZE,
  DEFAULT_MARKER_STYLE_ID, DEFAULT_TEXT_MARKER_OPACITY, ID_OF_NUMERICAL_MARKER,
  MARKER_DEFAULT_COLOR,
} from '~/_shared/utils/markers/markerVisualSettings.constants';
import { useBucketsWithRange } from '~/_shared/utils/markers/useBucketsWithRange.helpers';
import { type ModalProps } from '~/modal/modalType.enum';
import { mapToolTitleMap } from '~/sidebar/sidebarApps/mapTools/mapTools.helpers';
import { openMapTool } from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { useMapSettingsGroupingActiveGroupColumnsSelector } from '~/store/mapSettings/grouping/mapSettingsGrouping.selectors';
import {
  mapSettingsUpdateMarkersGeneral, removeGroupMarkerSettings, removeIndividualMarkerSettings,
  setIndividualMarkerSettings, setNumericGroupBucketMarkerSettings, setTextGroupMarkerSettings,
} from '~/store/mapSettings/makersGeneral/mapSettingsMarkersGeneral.actionCreators';
import { useMapSettingsMarkersGeneralSelector } from '~/store/mapSettings/makersGeneral/mapSettingsMarkersGeneral.selectors';
import { useMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import {
  getGroupingColumnsUniqueGroups,
  NON_NUMERICAL_VALUE,
  type NumericBucketWithRange,
} from '~/store/spreadsheetData/grouping/spreadsheetData.grouping.helpers';
import { useSpreadsheetDataDataSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import { emptySpreadsheetData } from '~/store/spreadsheetData/spreadsheetData.state';
import { trackUserEvent } from '~/store/userEvents/userEvents.actionCreator';
import { createUserEvent } from '~/store/userEvents/userEvents.helper';
import { isGroupPrimary } from '../grouping/groupingColumns.helpers';
import { CustomizationLevel } from './customizeLabelAndMarker.types';
import { CustomizeLabelAndMarkerSettingsModalContainer } from './customizeLabelAndMarkerSettingsModal.container';
import { type CustomizeMarkerTab } from './customizeMarker/customizeMarker.component';

export type CustomizationLevelProps = Readonly<{
  availableCustomizationLevels: { value: CustomizationLevel; name: string }[];
  customizationLevel: CustomizationLevel;
  setCustomizationLevel: (newLevel: CustomizationLevel) => void;
  overrideGroupMarkers: boolean;
  setOverrideGroupMarkers: ((overrideGroupMarkers: boolean) => void) | null;
}>;

type CustomizationLevelDependentData = {
  caption: string;
  groupBuckets?: NumericBucketWithRange[];
  markerSettingsTypes: NonEmptyArray<MarkerSettingType>;
  optionalLabelDisabled?: boolean;

  onReset: (visuals: MarkersVisualSettings) => MarkersVisualSettings; //kept this mechanism here if we wanted to show the marker after reset in the future
  onSave: (settings: MarkerSettings | null) => void;
};

export type CustomizeLabelAndMarkerModalContainerProps = ModalProps<{
  customizationLevel: CustomizationLevel;
  overrideGroupMarkers?: boolean;
  defaultTab?: CustomizeMarkerTab;
  groupInfo?: Readonly<{
    bucketId?: number;
    groupId?: string;
    groupingColumn: GroupingColumn;
    hierarchyIndicator?: number;
  }>;
  markerSettingsTypes: NonEmptyArray<MarkerSettingType>;
  spreadsheetRowId?: SpreadsheetRowId;
}>;

export const CustomizeLabelAndMarkerModalContainer: React.FC<CustomizeLabelAndMarkerModalContainerProps> = props => {
  const { markerSettingsTypes, spreadsheetRowId, onClose } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();
  const markersVisualSettings = useMapSettingsMarkersGeneralSelector();
  const groupingColumns = useMapSettingsGroupingActiveGroupColumnsSelector();
  const spreadsheetData = useSpreadsheetDataDataSelector();
  const matchupData = useMatchupDataSelector();
  const areLabelsActive = !!markersVisualSettings.useTextLabel || !!markersVisualSettings.useNumericLabel;
  const isGroupingActive = !!groupingColumns[0];
  const isLabelsAboveCustomization = markerSettingsTypes?.length === 1 && markerSettingsTypes[0] === MarkerSettingType.LabelAbove;

  const [selectedCustomizationLevel, setSelectedCustomizationLevel] = useState<CustomizationLevel>(props.customizationLevel);
  const [overrideGroupMarkers, setOverrideGroupMarkers] = useState<boolean>(!!props.overrideGroupMarkers);

  const individualMarkerGroupInfo = useGetGroupInfoBySpreadsheetRowId(spreadsheetRowId, props.groupInfo?.hierarchyIndicator || 0);
  const currentDefaultSecondaryTextMarkerStyleId = getDefaultStyleIdForSecondaryTextGroup(markersVisualSettings);

  const selectGroupProps = useSelectGroupingToolGroup();

  const groupInfo: GroupInfo | null = useMemo(() => {
    if (props.groupInfo) {
      if (isGroupingColumnText(props.groupInfo.groupingColumn)) {
        return {
          groupingColumn: props.groupInfo.groupingColumn,
          groupId: props.groupInfo.groupId,
        } as TextGroupInfo;
      }
      if (isGroupingColumnNumeric(props.groupInfo.groupingColumn)) {
        return {
          groupingColumn: props.groupInfo.groupingColumn,
          bucketId: props.groupInfo.bucketId,
        } as NumericGroupInfo;
      }
    }
    else if (individualMarkerGroupInfo) {
      return individualMarkerGroupInfo;
    }
    else if (selectGroupProps.selectedGroupingColumn && selectGroupProps.isSelectionValid) {
      if (isGroupingColumnText(selectGroupProps.selectedGroupingColumn) && selectGroupProps.selectedGroupId !== null) {
        return {
          groupingColumn: selectGroupProps.selectedGroupingColumn,
          groupId: selectGroupProps.selectedGroupId,
        } as TextGroupInfo;
      }
      if (isGroupingColumnNumeric(selectGroupProps.selectedGroupingColumn) && selectGroupProps.selectedBucketId !== null) {
        return {
          groupingColumn: selectGroupProps.selectedGroupingColumn,
          bucketId: selectGroupProps.selectedBucketId,
        } as NumericGroupInfo;
      }
    }
    return null;
  }, [individualMarkerGroupInfo, props.groupInfo, selectGroupProps]);

  const groupColumnInfo = useMemo(() => {
    if (groupInfo) {
      return groupInfo.groupingColumn;
    }
    // if:
    //  - we access from customize all modal
    //  - and we don't have any selectGroupProps.selectedGroupingColumn selected
    // then groupInfo is not setbut how the modal behaves is still affected by the primary grouping column
    if (selectedCustomizationLevel === CustomizationLevel.All && groupingColumns[0]) {
      return groupingColumns[0];
    }
    return null;
  }, [groupInfo, groupingColumns, selectedCustomizationLevel]);

  const currentlyUsedGroupingColumn = useMemo(() => {
    const fromProps = props.groupInfo?.groupingColumn;
    const fromIndividual = individualMarkerGroupInfo?.groupingColumn;
    const fromSelection = selectGroupProps.selectedGroupingColumn;

    return fromProps ?? fromIndividual ?? fromSelection ?? groupColumnInfo ?? undefined;
  }, [props.groupInfo, individualMarkerGroupInfo, selectGroupProps.selectedGroupingColumn, groupColumnInfo]);

  const groupBuckets = useBucketsWithRange(currentlyUsedGroupingColumn);

  const isGroupColumnNumeric = groupColumnInfo && isGroupingColumnNumeric(groupColumnInfo);

  const handleSetCustomizationLevel = useCallback((newCustomizationLevel: CustomizationLevel) => {
    if (newCustomizationLevel === CustomizationLevel.Group && !isGroupingActive) {
      onClose();
      dispatch(openMapTool(MapTool.Grouping));
      return;
    }
    setSelectedCustomizationLevel(newCustomizationLevel);
  }, [dispatch, isGroupingActive, onClose]);

  const customizationLevelData: CustomizationLevelProps = useMemo(() => {
    let availableCustomizationLevels = new Set<CustomizationLevel>([]);
    let customizationLevel = selectedCustomizationLevel;
    switch (props.customizationLevel) {
      case CustomizationLevel.Individual:
        availableCustomizationLevels = new Set<CustomizationLevel>([
          CustomizationLevel.Individual,
          CustomizationLevel.Group,
          CustomizationLevel.All,
        ]);
        break;
      case CustomizationLevel.Group:
        availableCustomizationLevels = new Set<CustomizationLevel>([
          CustomizationLevel.All, CustomizationLevel.Group,
        ]);
        break;
      case CustomizationLevel.All:
      default:
        availableCustomizationLevels = new Set<CustomizationLevel>([
          CustomizationLevel.All,
          CustomizationLevel.Group,
        ]);
    }
    if (!availableCustomizationLevels.has(customizationLevel)) {
      customizationLevel = props.customizationLevel;
    }

    return {
      customizationLevel,
      overrideGroupMarkers,
      availableCustomizationLevels: Array.from(availableCustomizationLevels).map(value => ({
        value,
        name: value !== CustomizationLevel.Group
          ? t(value)
          : !isGroupingActive
            ? t('Add Grouping Column')
            : !props.groupInfo && !individualMarkerGroupInfo
              ? t('Group')
              : t(value),
      })),
      setCustomizationLevel: handleSetCustomizationLevel,
      // we cannot allow overriding group markers if the customization modal is invoked from the "All" level
      // as it leads to usability issues with not knowing which marker to display in the preview
      setOverrideGroupMarkers: isGroupingActive && props.customizationLevel !== CustomizationLevel.All
        ? setOverrideGroupMarkers
        : null,
    };
  }, [selectedCustomizationLevel, props.customizationLevel, props.groupInfo, overrideGroupMarkers,
    handleSetCustomizationLevel, isGroupingActive, t, individualMarkerGroupInfo,
  ]);

  const allowGroupSelection = isGroupingActive
      && customizationLevelData.customizationLevel === CustomizationLevel.Group
      && !props.groupInfo
      && !individualMarkerGroupInfo;

  const finalSelectGroupProps = useMemo(() => ({
    ...selectGroupProps,
    ...(!allowGroupSelection ? {
      setSelectedGroupingColumn: undefined,
      setSelectedUniqueGroup: undefined,
    } : {}),
  }), [allowGroupSelection, selectGroupProps]);

  const hierarchyIndicator = props.groupInfo?.hierarchyIndicator || (allowGroupSelection ? finalSelectGroupProps.selectedGroupingColumnHierarchyIndicator || 0 : 0);

  const allIndividualMarkersInGroup = useGetSpreadsheetRowIdsByGroupInfo(groupInfo);
  const groupingActiveAndCustomizingAll = isGroupingActive && customizationLevelData.customizationLevel === CustomizationLevel.All;

  const uniqueGroupColumns = useMemo(() => getGroupingColumnsUniqueGroups(
    spreadsheetData ?? emptySpreadsheetData,
    matchupData,
    groupColumnInfo || undefined,
  ), [matchupData, groupColumnInfo, spreadsheetData]);

  const numericActiveGroupColumnType = useMemo(() => (
    getNumericActiveGroupColumnType(groupingColumns)
  ), [groupingColumns]);

  const textGroupKey = useMemo(() => (
    groupInfo?.groupingColumn?.type === GroupingType.Text
      ? createTextGroupKey(
        (groupInfo as TextGroupInfo).groupId,
        isGroupPrimary(hierarchyIndicator)
      )
      : undefined
  ), [groupInfo, hierarchyIndicator]);

  const bucketKey = useMemo(() => (
    groupColumnInfo?.type === GroupingType.Numeric && numericActiveGroupColumnType
      ? createNumericBucketKey(
        numericActiveGroupColumnType, hierarchyIndicator, filterNonNumericBuckets(uniqueGroupColumns).length,
      )
      : undefined
  ), [groupColumnInfo, numericActiveGroupColumnType, hierarchyIndicator, uniqueGroupColumns]);

  const handleSaveIndividual = useCallback((settings: MarkerSettings | null) => {
    if (!props.spreadsheetRowId) {
      return;
    }
    if (settings) {
      dispatch(trackUserEvent(createUserEvent('IndividualMarkerCustomizationSaved')));
      dispatch(setIndividualMarkerSettings(props.spreadsheetRowId, removeAboveLabelVisualSettings(settings)));
    }
    else {
      dispatch(removeIndividualMarkerSettings([props.spreadsheetRowId]));
    }
  }, [dispatch, props.spreadsheetRowId]);

  const handleResetIndividual = useCallback((visuals: MarkersVisualSettings) => ({
    ...visuals,
    individualMarkerSettings: undefined,
  }), []);

  const handleSaveGroup = useCallback((settings: MarkerSettings | null) => {
    if (!groupInfo?.groupingColumn) {
      return;
    }

    if (!settings) {
      dispatch(removeGroupMarkerSettings([{
        spreadsheetId: groupInfo.groupingColumn.spreadsheetId,
        columnId: groupInfo.groupingColumn.columnId,
      }]));
      return;
    }

    const newSettingsWithoutIrrelevantLabelVisualSettings = removeAboveLabelVisualSettings(settings);
    const newSettingsWithSynchronizedColors = areLabelsActive ?
      newSettingsWithoutIrrelevantLabelVisualSettings :
      syncMarkerColorAndLabelBackgroundVisualSettings(newSettingsWithoutIrrelevantLabelVisualSettings);

    dispatch(trackUserEvent(createUserEvent('GroupMarkerCustomizationSaved')));

    if (textGroupKey) {
      dispatch(setTextGroupMarkerSettings({
        spreadsheetId: groupInfo.groupingColumn.spreadsheetId,
        columnId: groupInfo.groupingColumn.columnId,
        groupKey: textGroupKey,
        settings: newSettingsWithSynchronizedColors,
      }));
    }
    else if (bucketKey) {
      dispatch(setNumericGroupBucketMarkerSettings({
        spreadsheetId: groupInfo.groupingColumn.spreadsheetId,
        columnId: groupInfo.groupingColumn.columnId,
        bucketKey,
        bucketId: (groupInfo as NumericGroupInfo).bucketId,
        settings: newSettingsWithSynchronizedColors,
      }));
    }

    // remove any individually customized markers in this group
    // removing must be done last, otherwise syncActiveGroupMarkerAttachmentsSaga would remove added textures above
    if (allIndividualMarkersInGroup.length) {
      dispatch(removeIndividualMarkerSettings(allIndividualMarkersInGroup));
    }
  }, [areLabelsActive, groupInfo, textGroupKey, bucketKey, allIndividualMarkersInGroup, dispatch]);

  const handleResetGroup = useCallback((visuals: MarkersVisualSettings) => ({
    ...visuals,
    groupMarkerSettings: undefined,
  }), []);

  const handleSaveCustomizeAll = useCallback((settings: MarkerSettings | null) => {
    const maybeUpdateDefaultMarker =
      isGroupPrimary(hierarchyIndicator)
      && settings?.marker?.styleType === MarkerStyleType.STANDARD
      && !groupBuckets
        ? { defaultMarkerSetId: settings.marker?.styleId }
        : undefined;

    // if the modal was invoked for non-primary grouping column
    // and "all markers" was selected in the dropdown
    // we want to change the default marker for that grouping column
    let maybeUpdateGroupMarkerDefaultMarkerSetIds = undefined;
    let maybeUpdateGroupsDefaultMarker = undefined;
    const newMarkerSettings = settings?.marker;

    if (groupColumnInfo && newMarkerSettings && !isLabelsAboveCustomization) {

      const spreadsheetId = groupColumnInfo.spreadsheetId;
      const columnId = groupColumnInfo.columnId;

      if (groupColumnInfo.type === GroupingType.Text
        && isStandardMarkerVisualSettings(newMarkerSettings)
        && !isGroupPrimary(hierarchyIndicator) //primary text is handled by deleting customizations lower
      ) {

        maybeUpdateGroupMarkerDefaultMarkerSetIds = {
          groupMarkerDefaultMarkerSetIds: {
            ...markersVisualSettings.groupMarkerDefaultMarkerSetIds,
            [hierarchyIndicator]: {
              ...markersVisualSettings.groupMarkerDefaultMarkerSetIds?.[hierarchyIndicator],
              [GroupingType.Text]: {
                ...markersVisualSettings.groupMarkerDefaultMarkerSetIds?.[hierarchyIndicator]?.[GroupingType.Text],
                styleId: newMarkerSettings.styleId,
              },
            },
          },
        };

        const existing = markersVisualSettings.groupMarkerSettings?.[spreadsheetId]?.[columnId]?.[GroupingType.Text];
        if (existing) {
          maybeUpdateGroupsDefaultMarker = {
            groupMarkerSettings: {
              ...markersVisualSettings.groupMarkerSettings,
              [spreadsheetId]: {
                ...markersVisualSettings.groupMarkerSettings?.[spreadsheetId],
                [columnId]: {
                  ...markersVisualSettings.groupMarkerSettings?.[spreadsheetId]?.[columnId],
                  [GroupingType.Text]: {
                    ...existing,
                    ...Object.entries(existing).reduce<{ [bucketId: TextGroupKey ]: MarkerSettings }>((acc, [key, value]) => {
                      if (getTextGroupKeyHierarchyIndicator(key) === hierarchyIndicator && value?.marker
                        // if the marker was customized, we don't want to override it, unless overrideGroupMarkers is on
                        && (overrideGroupMarkers
                          || (isStandardMarkerVisualSettings(value.marker) && value.marker.styleId === currentDefaultSecondaryTextMarkerStyleId)
                        )
                      ) {
                        acc[key] = {
                          ...value,
                          marker: {
                            ...value.marker,
                            styleType: MarkerStyleType.STANDARD,
                            styleId: newMarkerSettings.styleId,
                            size: newMarkerSettings.size,
                          },
                        };
                      }
                      return acc;
                    }, {}),
                  },
                },
              },
            },
          };
        }
      }
      else if (groupColumnInfo.type === GroupingType.Numeric && bucketKey) {

        const existing = markersVisualSettings.groupMarkerSettings?.[spreadsheetId]?.[columnId]?.[GroupingType.Numeric]?.[bucketKey];

        const defaultBuckets = Array.from(
          { length: groupColumnInfo.buckets.length + 1 },
          (_, bucketIndex) => getDefaultMarkerSettingsForGroup({
            activeGroupColumns: groupingColumns,
            buckets: groupBuckets,
            divisionIndex: bucketIndex,
            isLegend: false,
            hierarchyIndicator,
            type: GroupingType.Numeric,
            visualMarkersSettings: markersVisualSettings,
          })
        );

        const defaultBucketsWithExisting = defaultBuckets.map((defaultBucket, bucketIndex) => {
          const existingValue = existing?.[bucketIndex];
          return existingValue ? { ...defaultBucket, ...existingValue } : defaultBucket;
        });

        const mostPrevalentStyle = defaultBucketsWithExisting.reduce((acc, value) => {
          if (value?.marker) {
            if (isStandardMarkerVisualSettings(value.marker)) {
              acc.standard[value.marker.styleId] = (acc.standard[value.marker.styleId] || 0) + 1;
            }
            if (isCustomMarkerVisualSettings(value.marker)) {
              acc.custom[value.marker.fileAttachmentId] = (acc.custom[value.marker.fileAttachmentId] || 0) + 1;
            }
          }
          return acc;
        }, { standard: {}, custom: {} } as { standard: {[styleId: number]: number}; custom: {[id: string]: number} });
        const mostPrevalentStandardStyleId = Object.entries(mostPrevalentStyle.standard).reduce((acc, [key, value]) => {
          return value > acc[1] ? [parseInt(key, 10), value] : acc;
        }, [ID_OF_NUMERICAL_MARKER, 0] as const)[0];
        const mostPrevalentCustomId = Object.entries(mostPrevalentStyle.custom).reduce((acc, [key, value]) => {
          return value > acc[1] ? [key, value] : acc;
        }, ['X', 0] as const)[0];

        if (defaultBuckets.length) {
          maybeUpdateGroupsDefaultMarker = {
            groupMarkerSettings: {
              ...markersVisualSettings.groupMarkerSettings,
              [spreadsheetId]: {
                ...markersVisualSettings.groupMarkerSettings?.[spreadsheetId],
                [columnId]: {
                  ...markersVisualSettings.groupMarkerSettings?.[spreadsheetId]?.[columnId],
                  [GroupingType.Numeric]: {
                    ...markersVisualSettings.groupMarkerSettings?.[spreadsheetId]?.[columnId]?.[GroupingType.Numeric],
                    [bucketKey]: {
                      ...existing,
                      ...defaultBuckets.reduce<{[bucketId: BucketKey]: MarkerSettings }>((acc, defaultValue, bucketIndex) => {

                        const existingValue = existing?.[bucketIndex];
                        const finalValue = existingValue ?? defaultValue;

                        if (bucketIndex !== NON_NUMERICAL_VALUE && finalValue
                        // if the marker was customized, we don't want to override it, unless overrideGroupMarkers is on
                        && (overrideGroupMarkers
                          // or because it's not been set and we don't want to to be set to default (we want to set what user sees)
                          || !existingValue
                          // or because it's using the most prevalent setting in which case we assume that's the marker user wants to change
                          || (existingValue?.marker && isStandardMarkerVisualSettings(existingValue.marker) && [ID_OF_NUMERICAL_MARKER, mostPrevalentStandardStyleId].includes(existingValue.marker.styleId))
                          || (existingValue?.marker && isCustomMarkerVisualSettings(existingValue.marker) && existingValue.marker.fileAttachmentId === mostPrevalentCustomId)
                        )) {
                          acc[bucketIndex] = {
                            ...finalValue,
                            marker: {
                              ...finalValue.marker || {
                                size: DEFAULT_MARKER_SIZE,
                              },
                              ...(isCustomMarkerVisualSettings(newMarkerSettings) ? {
                                styleType: MarkerStyleType.CUSTOM,
                                fileAttachmentId: newMarkerSettings.fileAttachmentId,
                              } : {
                                styleType: MarkerStyleType.STANDARD,
                                styleId: newMarkerSettings.styleId,
                              }),
                              selectedColor: newMarkerSettings.selectedColor,
                              opacity: newMarkerSettings.opacity,
                            },
                          };
                        }
                        return acc;
                      }, {}),
                    },
                  },
                },
              },
            },
          };
        }
      }
    }

    dispatch(mapSettingsUpdateMarkersGeneral({
      ...(settings && ((isGroupPrimary(hierarchyIndicator) && !groupBuckets) || isLabelsAboveCustomization) && {
        globalMarkerSettings: {
          ...settings,
          ...settings.marker && {
            marker: {
              ...settings.marker,
              // Global marker COLOR is not customizable when grouping is enabled and the active column is TEXT
              ...(!isLabelsAboveCustomization && groupingActiveAndCustomizingAll && !isGroupColumnNumeric && {
                selectedColor: markersVisualSettings.globalMarkerSettings?.marker?.selectedColor || MARKER_DEFAULT_COLOR,
                opacity: markersVisualSettings.globalMarkerSettings?.marker?.opacity || DEFAULT_TEXT_MARKER_OPACITY,
              }),
            },
          },
        },
      }),
      ...maybeUpdateDefaultMarker,
      ...maybeUpdateGroupsDefaultMarker,
      ...maybeUpdateGroupMarkerDefaultMarkerSetIds,
    }));

    // overriding marker style should work only for text groups, numerical are adjusted above
    // removing must be done last, otherwise syncActiveGroupMarkerAttachmentsSaga would remove added textures above
    if (overrideGroupMarkers && groupingColumns && !!groupingColumns[0] && !isGroupColumnNumeric) {
      dispatch(removeGroupMarkerSettings([{
        spreadsheetId: groupingColumns[0].spreadsheetId,
        columnId: groupingColumns[0].columnId,
      }]));
    }
  }, [bucketKey, dispatch, groupColumnInfo, groupingActiveAndCustomizingAll, groupingColumns,
    hierarchyIndicator, overrideGroupMarkers,
    currentDefaultSecondaryTextMarkerStyleId, isGroupColumnNumeric,
    markersVisualSettings, groupBuckets, isLabelsAboveCustomization,
  ]);

  const handleResetAll = useCallback((visuals: MarkersVisualSettings) => ({
    ...visuals,
    defaultMarkerSetId: DEFAULT_MARKER_STYLE_ID,
    globalMarkerSettings: undefined,
  }), []);

  const customizationLevelDependentData = useMemo((): CustomizationLevelDependentData => {
    switch (customizationLevelData.customizationLevel) {
      case CustomizationLevel.Individual:
        return {
          caption: t('Individual Markers Settings'),
          ...(groupInfo?.groupingColumn.type === GroupingType.Numeric && groupBuckets && { groupBuckets }),
          markerSettingsTypes: areLabelsActive
            ? [MarkerSettingType.Marker, MarkerSettingType.Label] : [MarkerSettingType.Marker],
          onReset: handleResetIndividual,
          onSave: handleSaveIndividual,
        };
      case CustomizationLevel.Group:
        return {
          caption: t('Group Markers Settings'),
          ...(groupInfo?.groupingColumn.type === GroupingType.Numeric && groupBuckets && { groupBuckets }),
          markerSettingsTypes: areLabelsActive && isGroupPrimary(hierarchyIndicator)
            ? [MarkerSettingType.Marker, MarkerSettingType.Label] : [MarkerSettingType.Marker],
          optionalLabelDisabled: !isGroupPrimary(hierarchyIndicator),
          onReset: handleResetGroup,
          onSave: handleSaveGroup,
        };
      case CustomizationLevel.All:
      default:
        return {
          caption: isLabelsAboveCustomization
            ? t('General Label Above Markers Settings')
            : markerSettingsTypes?.length === 1 && markerSettingsTypes[0] === MarkerSettingType.Label
              ? t('General Label Marker Settings') : t('General Marker Settings'),
          markerSettingsTypes,
          optionalLabelDisabled: isGroupingActive,
          onReset: handleResetAll,
          onSave: handleSaveCustomizeAll,
        };
    }
  }, [customizationLevelData, groupInfo?.groupingColumn, t, groupBuckets, areLabelsActive,
    handleResetIndividual, handleSaveIndividual, hierarchyIndicator, handleResetGroup, handleSaveGroup,
    markerSettingsTypes, handleResetAll, handleSaveCustomizeAll, isGroupingActive, isLabelsAboveCustomization,
  ]);

  // When coming from grouping tool the marker should by default look like the selected group marker
  // When coming from invididual marker, we still need bucket info to display the correct marker
  const forVisualizationGroupInfo = useMemo(() => (
    props.groupInfo ? {
      hierarchyIndicator: props.groupInfo.hierarchyIndicator,
      groupBuckets,
      groupId: props.groupInfo.groupId,
      bucketId: props.groupInfo.bucketId,
    } : (props.spreadsheetRowId || isGroupingActive) ? { groupBuckets } : undefined
  ), [groupBuckets, props.groupInfo, props.spreadsheetRowId, isGroupingActive]);

  const customizationsDisabledProps = useMemo(() => {
    if (groupingActiveAndCustomizingAll) {
      const disabledReason = t('markerCustomization.customizationDisabledReason.byTool', {
        toolName: t(mapToolTitleMap.Grouping),
      });
      if (isGroupColumnNumeric) {
        return {
          colorDisabledReason: undefined,
          sizeDisabledReason: disabledReason,
        };
      }
      else {
        return {
          colorDisabledReason: disabledReason,
          sizeDisabledReason: undefined,
        };
      }
    }

    return undefined;
  }, [groupingActiveAndCustomizingAll, isGroupColumnNumeric, t]);

  return (
    <CustomizeLabelAndMarkerSettingsModalContainer
      caption={customizationLevelDependentData.caption}
      customizationLevelProps={customizationLevelData}
      defaultTab={props.defaultTab}
      groupInfo={forVisualizationGroupInfo}
      isOpen={props.isOpen}
      visualMarkersSettings={markersVisualSettings}
      markerSettingsTypes={customizationLevelDependentData.markerSettingsTypes}
      optionalLabelDisabled={customizationLevelDependentData.optionalLabelDisabled}
      spreadsheetRowId={props.spreadsheetRowId}
      onClose={onClose}
      onReset={customizationLevelDependentData.onReset}
      onSave={!allowGroupSelection || finalSelectGroupProps.isSelectionValid ? customizationLevelDependentData.onSave : undefined}
      customizationsDisabled={customizationsDisabledProps}
      selectGroupProps={finalSelectGroupProps}
    />
  );
};
